import React, { useState } from 'react';
import { useReports } from '../../hooks/useReports';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { FilterBar } from '../../components/dashboard/FilterBar';
import { ExportButton } from '../../components/dashboard/ExportButton';
import { Download, FileSpreadsheet, BarChart3, PieChart, TrendingUp, DollarSign } from 'lucide-react';
import { LoadingSpinner } from '../../components/ui/Feedback';

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  Complaints: <BarChart3 className="w-4 h-4" />,
  Vendors: <PieChart className="w-4 h-4" />,
  Workforce: <TrendingUp className="w-4 h-4" />,
  Analytics: <BarChart3 className="w-4 h-4" />,
  Finance: <DollarSign className="w-4 h-4" />,
};

export const ReportsPage: React.FC = () => {
  const { reportTypes, reportData, loading, error, filter, setFilter, fetchReportData, exportReport } = useReports();
  const [selectedReport, setSelectedReport] = useState<{ id: string; name: string } | null>(null);

  const handleSelectReport = (report: { id: string; name: string }) => {
    setSelectedReport(report);
    fetchReportData(report.id);
  };

  const categories = [...new Set(reportTypes.map(r => r.category))];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <FilterBar
          filter={filter}
          onChange={setFilter}
          showDepartment
          showStatus
          showPriority
          departments={[
            { id: 'Production', name: 'Production' },
            { id: 'Maintenance', name: 'Maintenance' },
            { id: 'Facilities', name: 'Facilities' },
            { id: 'IT', name: 'IT' },
          ]}
        />
        {selectedReport && reportData.length > 0 && (
          <ExportButton
            config={{
              filename: `${selectedReport.id}-${new Date().toISOString().split('T')[0]}`,
              sheetName: selectedReport.name,
              columns: Object.keys(reportData[0]).map(key => ({
                key,
                header: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
                width: 18,
              })),
              data: reportData,
              filters: filter,
              generatedAt: new Date().toISOString(),
              generatedBy: 'Reports',
            }}
          />
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.map(category => (
          <Card key={category} title={category} subtitle={`${reportTypes.filter(r => r.category === category).length} reports`}>
            <div className="space-y-2">
              {reportTypes.filter(r => r.category === category).map(report => (
                <button
                  key={report.id}
                  onClick={() => handleSelectReport(report)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                    selectedReport?.id === report.id
                      ? 'bg-sky-500/10 border-sky-500/30'
                      : 'bg-slate-900/40 border-slate-800 hover:bg-slate-800/60'
                  }`}
                >
                  <div className="p-2 bg-slate-800 rounded-lg">
                    {CATEGORY_ICONS[category] || <FileSpreadsheet className="w-4 h-4 text-slate-400" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-200 truncate">{report.name}</p>
                    <p className="text-xs text-slate-500 truncate">{report.description}</p>
                  </div>
                </button>
              ))}
            </div>
          </Card>
        ))}
      </div>

      {selectedReport && (
        <Card title={`${selectedReport.name} — Results`} subtitle={`${reportData.length} rows`}>
          {loading ? (
            <LoadingSpinner />
          ) : error ? (
            <div className="text-rose-400 text-sm p-4">{error}</div>
          ) : reportData.length === 0 ? (
            <div className="text-center py-8 text-slate-500 text-sm">No data available for this report</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700">
                    {Object.keys(reportData[0]).map(key => (
                      <th key={key} className="text-left py-3 px-4 text-xs font-medium text-slate-400">
                        {key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {reportData.map((row, i) => (
                    <tr key={i} className="border-b border-slate-800 hover:bg-slate-800/50">
                      {Object.values(row).map((val, j) => (
                        <td key={j} className="py-3 px-4 text-slate-300">
                          {String(val)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};
